import {
  ColDef,
  IDateFilterParams,
  INumberFilterParams,
  ISetFilterParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  addThousandsComma,
  amiComparator,
  amiFilterTextFormatter,
  currencyFormatter,
  dateComparator,
  dateFormatter,
  parseProgress,
} from './ApplicantsTableUtils';
import React from 'react';
import AGCategoryStatus, { AGCategoryStatusProps } from './AGCategoryStatus';
import AGDatePicker from './AGDatePicker';
import AGFileOwner, { AGFileOwnerProps } from './AGFileOwner';
import AGWatchCellRenderer from './AGWatchCellRenderer';
import { Link } from 'react-router';

export const DATE_FILTER_PARAMS: IDateFilterParams = {
  comparator: dateComparator,
  buttons: ['apply', 'clear'],
  closeOnApply: true,
};

export const RentersDashboardColDefs: ColDef[] = [
  {
    headerName: 'Watching',
    field: 'is_watched',
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: (params) => {
        return params.value ? 'Watching' : 'Not Watching';
      },
    },
    sortable: true,
    minWidth: 120,
    cellRenderer: AGWatchCellRenderer,
    valueGetter: (_params) => {
      // For filtering and sorting, we'll use a placeholder value
      // The actual watch status is determined by the cell renderer
      return 'Yes'; // This will be overridden by the cell renderer
    },
  },
  {
    headerName: 'Head of Household',
    field: 'hoh_name',
    cellRenderer: (params) => {
      return (
        <Link to={params.data.certification_link}>{params.data.hoh_name}</Link>
      );
    },
    filter: true,
    sortable: true,
    wrapText: true,
    minWidth: 150,
  },
  {
    headerName: 'Reachable HoH',
    headerTooltip: 'HoH has at least one verified email or phone',
    field: 'reachable_hoh',
    filter: 'agSetColumnFilter',
    sortable: true,
    minWidth: 140,
    valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
  },
  {
    headerName: 'Household Size',
    field: 'household_size',
    filter: 'agNumberColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      closeOnApply: true,
    } as INumberFilterParams,
    sortable: true,
    minWidth: 150,
    valueGetter: (params) => {
      // Use filtered household size that excludes emergency contacts
      const householdMembers = params.data.household_members;
      if (!householdMembers || !Array.isArray(householdMembers)) {
        return params.data.household_size || 0;
      }

      return householdMembers.filter(
        (member) =>
          member.member_type?.name !== 'non-household member emergency contact'
      ).length;
    },
  },
  {
    headerName: 'Household Income',
    field: 'household_income',
    filter: 'agNumberColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      closeOnApply: true,
    } as INumberFilterParams,
    valueFormatter: (params) => currencyFormatter(params.data.household_income),
    sortable: true,
    minWidth: 150,
  },
  {
    headerName: 'Log Number',
    field: 'log_number',
    filter: 'agNumberColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      closeOnApply: true,
    } as INumberFilterParams,
    valueFormatter: (params) => addThousandsComma(params.data.log_number),
    sortable: true,
    minWidth: 140,
  },
  {
    headerName: 'Batch Number',
    field: 'batch_number',
    // ENG-3191: feed the Set Filter the per-cert batch array so each batch is
    // its own option and selecting one matches any cert that carries it. The
    // array is what ag-grid's Set Filter flattens; the formatter keeps the
    // cell readable.
    valueGetter: (params: ValueGetterParams) => {
      const batchList = params.data?.batch_numbers;
      if (Array.isArray(batchList) && batchList.length > 0) {
        return batchList;
      }
      return params.data?.batch_number ? [params.data.batch_number] : [];
    },
    valueFormatter: (params: ValueFormatterParams) =>
      Array.isArray(params.value) && params.value.length > 0
        ? params.value.join(', ')
        : '',
    filter: 'agSetColumnFilter',
    sortable: true,
    wrapText: true,
    minWidth: 140,
  },
  {
    headerName: 'Preference Groups',
    field: 'preference_groups',
    filter: 'agSetColumnFilter',
    filterParams: {
      enableAndOr: true,
    },
    sortable: true,
    wrapText: true,
    valueFormatter: (params) => {
      if (
        !params.value ||
        (Array.isArray(params.value) && params.value.length === 0)
      ) {
        return '';
      }
      if (Array.isArray(params.value)) {
        // Handle corrupted data where name is literally "[object Object]"
        const cleanedValues = params.value.map((item) => {
          if (typeof item === 'string') {
            return item === '[object Object]'
              ? 'Corrupted Preference Group'
              : item;
          }
          if (typeof item === 'object' && item !== null) {
            if (item.name && item.name !== '[object Object]') {
              return item.name;
            }
            if (item.label && item.label !== '[object Object]') {
              return item.label;
            }
            if (item.id) {
              return `Preference Group (ID: ${item.id})`;
            }
            return 'Unknown Preference Group';
          }
          return String(item);
        });

        return cleanedValues.join(', ');
      }

      // Handle single value
      if (typeof params.value === 'string') {
        return params.value === '[object Object]'
          ? 'Corrupted Preference Group'
          : params.value;
      }

      return String(params.value);
    },
    minWidth: 150,
  },
  {
    headerName: 'Property Name',
    field: 'property_name',
    cellRenderer: (params) => {
      // Ensure we don't get double slashes by properly handling the property_link
      const baseUrl = params.data.property_link.endsWith('/')
        ? params.data.property_link.slice(0, -1)
        : params.data.property_link;
      const fullUrl = `${baseUrl}/available-units`;

      return <Link to={fullUrl}>{params.data.property_name}</Link>;
    },
    filter: 'agSetColumnFilter',
    wrapText: true,
    sortable: true,
    minWidth: 135,
  },
  {
    headerName: 'Portfolio',
    field: 'portfolio_name',
    filter: 'agSetColumnFilter',
    sortable: true,
    wrapText: true,
    minWidth: 150,
    valueGetter: (params) => {
      return params.data?.portfolio_name || '';
    },
  },
  {
    headerName: 'Owner',
    field: 'owner_name',
    filter: 'agSetColumnFilter',
    sortable: true,
    wrapText: true,
    minWidth: 150,
    valueGetter: (params) => {
      return params.data?.owner_name || '';
    },
  },
  {
    headerName: 'Building Name',
    field: 'building_name',
    filter: 'agSetColumnFilter',
    sortable: true,
    minWidth: 135,
  },
  {
    headerName: 'Building Number',
    field: 'building_number',
    filter: 'agSetColumnFilter',
    sortable: true,
    minWidth: 135,
  },
  {
    headerName: 'Unit Name',
    field: 'unit_name',
    filter: 'agSetColumnFilter',
    sortable: true,
    minWidth: 120,
  },
  {
    headerName: 'Unit Size',
    field: 'unit_size',
    filter: 'agNumberColumnFilter',
    sortable: true,
    minWidth: 115,
  },
  {
    headerName: 'Unit Type',
    field: 'unit_type',
    filter: 'agSetColumnFilter',
    sortable: true,
    minWidth: 115,
  },
  {
    headerName: 'Unit Programs',
    field: 'unit_programs',
    filter: 'agSetColumnFilter',
    sortable: true,
    wrapText: true,
    valueFormatter: (params) => {
      if (!params.value) {
        return '';
      }
      return params.value;
    },
    minWidth: 140,
  },
  {
    headerName: 'Applicant Pool Name',
    field: 'applicant_pool_name',
    filter: 'agSetColumnFilter',
    wrapText: true,
    sortable: true,
    minWidth: 150,
  },
  {
    headerName: 'AMI',
    field: 'amis',
    filter: 'agSetColumnFilter',
    sortable: true,
    valueFormatter: (params) => {
      if (!params.value) {
        return '';
      }
      return params.value;
    },
    cellStyle: { whiteSpace: 'pre-wrap' },
    minWidth: 110,
  },
  {
    headerName: 'Unit AMI',
    field: 'unit_ami',
    // ENG-3803: unit_ami is the AMI name (e.g. "60%"), a string — a number
    // filter parses it to NaN and matches nothing. Match the sibling AMI column.
    // ENG-3598: sort and filter by the numeric AMI, not the display string.
    filter: 'agSetColumnFilter',
    filterParams: {
      comparator: amiComparator,
      textFormatter: amiFilterTextFormatter,
    } as ISetFilterParams,
    comparator: amiComparator,
    sortable: true,
    minWidth: 110,
  },
  {
    headerName: 'File Owner',
    field: 'compliance_user_name',
    filter: 'agSetColumnFilter',
    editable: true,
    sortable: true,
    minWidth: 180,
    cellRenderer: AGFileOwner,
    cellRendererParams: (params): AGFileOwnerProps => {
      return {
        certificationId: params.data.id,
        complianceUserName: params.value,
        propertyId: params.data.property_id,
      };
    },
  },
  {
    headerName: 'Certification Name',
    field: 'certification_name',
    filter: 'agSetColumnFilter',
    wrapText: true,
    sortable: true,
    minWidth: 160,
  },
  {
    headerName: 'Category Name',
    field: 'category_name',
    filter: 'agSetColumnFilter',
    sortable: true,
    wrapText: true,
    minWidth: 140,
  },
  {
    headerName: 'Status',
    field: 'status_name',
    filter: 'agSetColumnFilter',
    wrapText: true,
    sortable: true,
    minWidth: 150,
    cellRenderer: AGCategoryStatus,
    cellRendererParams: (params): AGCategoryStatusProps => {
      return {
        categoryId: params.data.category_id,
        certificationId: params.data.id,
        statusId: params.data.status_id,
        statusName: params.data.status_name,
        isApproved: params.data.is_approved,
        isTerminal: params.data.is_terminal,
      };
    },
  },
  {
    headerName: 'Questionnaire Progress',
    field: 'questionnaire_progress',
    filter: 'agNumberColumnFilter',
    sortable: true,
    minWidth: 170,
    cellRenderer: (params) => {
      return <span className="progressColor">{Math.floor(params.value)}%</span>;
    },
    cellClassRules: {
      noProgressColor: (params) => {
        return params.data.questionnaire_progress === 0;
      },
      mediumProgressColor: (params) => {
        return (
          params.data.questionnaire_progress > 0 &&
          params.data.questionnaire_progress <= 70
        );
      },
      goodProgressColor: (params) => {
        return params.data.questionnaire_progress > 70;
      },
    },
  },
  {
    headerName: 'Total Progress',
    field: 'total_progress',
    filter: 'agNumberColumnFilter',
    sortable: true,
    minWidth: 150,
    cellRenderer: (params) => {
      return <span className="progressColor">{Math.floor(params.value)}%</span>;
    },
    cellClassRules: {
      noProgressColor: (params) => {
        const progress = parseProgress(params.data.total_progress);
        return progress === 0;
      },
      mediumProgressColor: (params) => {
        const progress = parseProgress(params.data.total_progress);
        return progress > 0 && progress <= 70;
      },
      goodProgressColor: (params) => {
        const progress = parseProgress(params.data.total_progress);
        return progress > 70;
      },
    },
  },
  {
    headerName: 'Unread Communications',
    field: 'unread_communications',
    filter: 'agNumberColumnFilter',
    cellRenderer: (params) => {
      return (
        <Link to={`${params.data.certification_link}communications`}>
          {params.data.unread_communications}
        </Link>
      );
    },
    sortable: true,
    minWidth: 185,
  },
  {
    headerName: 'Effective Date',
    field: 'effective_date',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.effective_date),
    sortable: true,
    minWidth: 135,
  },
  {
    headerName: 'Deadline',
    field: 'deadline_date',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.deadline_date),
    cellRenderer: AGDatePicker,
    cellRendererParams: (params) => {
      return {
        certificationId: params.data.id,
      };
    },
    sortable: true,
    minWidth: 145,
  },
  {
    headerName: 'Lease Start',
    field: 'lease_start',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.lease_start),
    sortable: true,
    minWidth: 120,
  },
  {
    headerName: 'Lease End',
    field: 'lease_end',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.lease_end),
    sortable: true,
    minWidth: 120,
  },
  {
    headerName: 'Move In Date',
    field: 'move_in_date',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.move_in_date),
    sortable: true,
    minWidth: 120,
  },
  {
    headerName: 'Move Out Date',
    field: 'move_out_date',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    valueFormatter: (params) => dateFormatter(params.data.move_out_date),
    sortable: true,
    minWidth: 120,
  },
  {
    headerName: 'Latest Status Note',
    field: 'latest_status_note',
    filter: 'agTextColumnFilter',
    sortable: true,
    wrapText: true,
    minWidth: 200,
    cellRenderer: (params) => {
      return (
        <span className="latestNote">{params.data.latest_status_note}</span>
      );
    },
  },
];
